import { useCallback } from 'react';
import { useQuery } from 'react-query';
import { format } from 'date-fns';
import { useAxios } from '../context/AxiosContext';
import { IncomingGroupDataModifier } from '../utils/groups/incomingDataTransformation';
import { downloadFileFromResponse } from '../utils/fileDownload';
import { GroupsPackage, TransformedGroupsPackage } from '../@types/Groups';

export function useGroupPage({ groupId, type, setInitialValues }) {
  const { authAxios } = useAxios();
  const entity: string = type === 'draft' ? 'draft' : 'group';

  const fetchLogo = useCallback(
    async (id?: string) => {
      const response = await authAxios.get(`/${entity}/${id}/logo`);
      if (response?.data?.file) {
        const string = 'data:image/jpeg;base64,' + response.data.file;
        return [{ preview: string }];
      }
    },
    [authAxios, entity]
  );

  const exportDrafts = useCallback(async () => {
    await authAxios.get('/draft/export').then((response) => {
      downloadFileFromResponse(response, `group_drafts_${format(new Date(), 'MMddyyyy')}.csv`);
    });
  }, [authAxios]);

  const fetchFormData = useCallback(
    async (id?: string) => {
      const modifier: IncomingGroupDataModifier = new IncomingGroupDataModifier();
      const { data } = await authAxios.get<GroupsPackage>(`/${entity}/${id}`);
      const logo = await fetchLogo(id);
      const groupData = modifier.transform(data);
      return { ...groupData, logo } as unknown as TransformedGroupsPackage;
    },
    [authAxios, entity, fetchLogo]
  );
  const groupData = useQuery(['form', type, groupId], () => fetchFormData(groupId), {
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    enabled: Boolean(entity && groupId),
    onSuccess: (data) => setInitialValues(data)
  });

  return {
    groupData,
    exportDrafts
  };
}
